import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ChatWidget from '@/components/feature/ChatWidget';
import {
  kpiData,
  energyByType,
  energyByArea,
  hourlyEnergyData,
  weeklyCarbonData,
  deviceStatus,
  alerts,
  carbonBreakdown,
  monthlyTrend,
} from '@/mocks/dashboardData';

/* ─── SVG Sparkline ─── */
function Sparkline({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 120;
  const h = 40;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={w} height={h} className="opacity-60">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        fill={`url(#grad-${color.replace('#', '')})`}
        points={`0,${h} ${points} ${w},${h}`}
      />
    </svg>
  );
}

/* ─── SVG Bar Chart ─── */
function BarChart({ data, color }: { data: { label: string; value: number }[]; color: string }) {
  const max = Math.max(...data.map((d) => d.value));
  const h = 160;
  const barW = 28;
  const gap = 16;
  const totalW = data.length * (barW + gap);

  return (
    <svg width="100%" height={h} viewBox={`0 0 ${totalW} ${h}`} preserveAspectRatio="none">
      {data.map((d, i) => {
        const barH = (d.value / max) * (h - 24);
        const x = i * (barW + gap);
        const y = h - barH - 20;
        return (
          <g key={d.label}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={barH}
              rx={4}
              fill={color}
              opacity={0.8}
            />
            <text
              x={x + barW / 2}
              y={h - 4}
              textAnchor="middle"
              fill="#6B7280"
              fontSize="10"
            >
              {d.label}
            </text>
            <text
              x={x + barW / 2}
              y={y - 4}
              textAnchor="middle"
              fill="#B0B8C1"
              fontSize="10"
            >
              {d.value}%
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ─── SVG Line Chart ─── */
function LineChart({ data, color, showArea = true }: { data: { x: string; y: number }[]; color: string; showArea?: boolean }) {
  const max = Math.max(...data.map((d) => d.y));
  const min = Math.min(...data.map((d) => d.y));
  const range = max - min || 1;
  const w = 600;
  const h = 200;
  const pad = { top: 10, bottom: 30, left: 0, right: 0 };

  const points = data.map((d, i) => {
    const x = pad.left + (i / (data.length - 1)) * (w - pad.left - pad.right);
    const y = pad.top + (h - pad.top - pad.bottom) - ((d.y - min) / range) * (h - pad.top - pad.bottom);
    return { x, y, label: d.x };
  });

  const linePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
  const areaPoints = `${points[0].x},${h - pad.bottom} ${linePoints} ${points[points.length - 1].x},${h - pad.bottom}`;

  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      {showArea && (
        <polygon
          fill={color}
          opacity="0.15"
          points={areaPoints}
        />
      )}
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        points={linePoints}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3" fill={color} />
          {i % 4 === 0 && (
            <text x={p.x} y={h - 8} textAnchor="middle" fill="#6B7280" fontSize="9">
              {p.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

/* ─── SVG Donut Chart ─── */
function DonutChart({ data, size = 140 }: { data: { name: string; value: number; color: string }[]; size?: number }) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const radius = size / 2 - 10;
  const center = size / 2;
  let currentAngle = -Math.PI / 2;

  return (
    <svg width={size} height={size}>
      {data.map((d) => {
        const angle = (d.value / total) * Math.PI * 2;
        const startAngle = currentAngle;
        const endAngle = currentAngle + angle;
        currentAngle = endAngle;

        const x1 = center + radius * Math.cos(startAngle);
        const y1 = center + radius * Math.sin(startAngle);
        const x2 = center + radius * Math.cos(endAngle);
        const y2 = center + radius * Math.sin(endAngle);
        const largeArc = angle > Math.PI ? 1 : 0;

        return (
          <path
            key={d.name}
            d={`M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
            fill={d.color}
            opacity={0.85}
            stroke="#0A1628"
            strokeWidth="2"
          />
        );
      })}
      <circle cx={center} cy={center} r={radius * 0.55} fill="#1E2530" />
      <text x={center} y={center - 4} textAnchor="middle" fill="#fff" fontSize="14" fontWeight="600">
        {total}%
      </text>
      <text x={center} y={center + 12} textAnchor="middle" fill="#6B7280" fontSize="9">
        占比
      </text>
    </svg>
  );
}

/* ─── KPI Card ─── */
function KpiCard({ kpi, index }: { kpi: typeof kpiData[0]; index: number }) {
  const [liveValue, setLiveValue] = useState(kpi.value);
  const colorMap: Record<string, string> = {
    green: '#00D9A3',
    blue: '#00B4D8',
    red: '#EF4444',
  };
  const color = colorMap[kpi.color] || '#00D9A3';

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveValue((prev) => {
        const variation = (Math.random() - 0.5) * (kpi.value * 0.02);
        return Math.max(0, Number((prev + variation).toFixed(kpi.unit === 'kWh' ? 1 : kpi.unit === '%' ? 1 : 0)));
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [kpi.value, kpi.unit]);

  const formatValue = (v: number) => {
    if (kpi.unit === '元') return `¥${v.toLocaleString()}`;
    if (kpi.unit === '%') return `${v.toFixed(1)}%`;
    return `${v.toLocaleString()} ${kpi.unit}`;
  };

  return (
    <div
      className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl p-5 hover:border-firmai-green/20 transition-all duration-300 group"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-firmai-dark border border-firmai-border text-firmai-green">
            <i className={`${kpi.icon} text-lg`} />
          </span>
          <span className="text-firmai-muted text-xs">{kpi.label}</span>
        </div>
        <div className={`flex items-center gap-1 text-xs ${kpi.change > 0 ? 'text-firmai-green' : kpi.change < 0 ? 'text-red-400' : 'text-firmai-muted'}`}>
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={kpi.change > 0 ? 'ri-arrow-up-line' : kpi.change < 0 ? 'ri-arrow-down-line' : 'ri-subtract-line'} />
          </span>
          {Math.abs(kpi.change)}%
        </div>
      </div>
      <div className="text-white font-display font-bold text-xl md:text-2xl mb-2">
        {formatValue(liveValue)}
      </div>
      <div className="text-firmai-muted text-xs mb-3">{kpi.changeLabel}</div>
      <Sparkline data={kpi.sparkline} color={color} />
    </div>
  );
}

export default function DashboardPage() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState<'overview' | 'devices' | 'carbon'>('overview');
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.05 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeTab]);

  const timeStr = currentTime.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = currentTime.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });

  return (
    <div className="min-h-screen bg-firmai-darker" ref={sectionRef}>
      <Navbar />

      <main className="pt-20">
        {/* Header Bar */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-4 border-b border-firmai-border bg-firmai-dark/80 backdrop-blur-sm sticky top-16 z-40">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <h1 className="text-white font-display font-bold text-lg md:text-xl">实时监测大屏</h1>
              <div className="flex items-center gap-2 px-3 py-1 bg-firmai-green/10 border border-firmai-green/20 rounded-full">
                <span className="w-2 h-2 bg-firmai-green rounded-full animate-pulse" />
                <span className="text-firmai-green text-xs font-medium">实时数据</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-white font-mono text-lg">{timeStr}</div>
              <div className="text-firmai-muted text-xs">{dateStr}</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4 p-1 bg-firmai-card border border-firmai-border rounded-lg w-fit">
            {[
              { id: 'overview' as const, label: '总览', icon: 'ri-dashboard-line' },
              { id: 'devices' as const, label: '设备监控', icon: 'ri-server-line' },
              { id: 'carbon' as const, label: '碳排放', icon: 'ri-leaf-line' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-firmai-green text-firmai-dark'
                    : 'text-firmai-text hover:text-white'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={tab.icon} />
                </span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ─── OVERVIEW TAB ─── */}
        {activeTab === 'overview' && (
          <div className="w-full px-4 md:px-8 lg:px-12 py-6 space-y-6">
            {/* KPI Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {kpiData.map((kpi, index) => (
                <KpiCard key={kpi.id} kpi={kpi} index={index} />
              ))}
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Hourly Energy */}
              <div className="lg:col-span-2 reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold text-sm md:text-base">今日能耗趋势（kWh）</h3>
                  <span className="text-firmai-muted text-xs">24小时</span>
                </div>
                <LineChart
                  data={hourlyEnergyData.map((d) => ({ x: d.hour, y: d.value }))}
                  color="#00D9A3"
                />
              </div>

              {/* Alerts */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold text-sm md:text-base">实时告警</h3>
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">{alerts.filter((a) => a.level !== 'info').length} 条待处理</span>
                </div>
                <div className="space-y-3 max-h-[200px] overflow-y-auto">
                  {alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        alert.level === 'critical'
                          ? 'bg-red-500/5 border-red-500/20'
                          : alert.level === 'warning'
                          ? 'bg-amber-500/5 border-amber-500/20'
                          : 'bg-firmai-dark/50 border-firmai-border'
                      }`}
                    >
                      <span className={`w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 ${
                        alert.level === 'critical' ? 'text-red-400' : alert.level === 'warning' ? 'text-amber-400' : 'text-firmai-green'
                      }`}>
                        <i className={alert.icon} />
                      </span>
                      <div className="min-w-0">
                        <div className="text-white text-xs font-medium">{alert.device}</div>
                        <p className="text-firmai-text text-xs mt-0.5 line-clamp-2">{alert.message}</p>
                        <span className="text-firmai-muted text-xs mt-1">{alert.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Energy by Type */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6">
                <h3 className="text-white font-semibold text-sm md:text-base mb-4">能耗类型分布</h3>
                <div className="flex items-center justify-center">
                  <DonutChart data={energyByType} />
                </div>
                <div className="mt-4 space-y-2">
                  {energyByType.map((item) => (
                    <div key={item.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-firmai-text text-xs">{item.name}</span>
                      </div>
                      <span className="text-white text-xs font-medium">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Energy by Area */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6">
                <h3 className="text-white font-semibold text-sm md:text-base mb-4">区域能耗占比</h3>
                <BarChart
                  data={energyByArea.map((d) => ({ label: d.name.replace('生产车间', '车间').replace('仓储物流', '仓储'), value: d.value }))}
                  color="#00B4D8"
                />
              </div>

              {/* Carbon Breakdown */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-200 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6">
                <h3 className="text-white font-semibold text-sm md:text-base mb-4">碳排放范围分布</h3>
                <div className="flex items-center justify-center">
                  <DonutChart data={carbonBreakdown} size={160} />
                </div>
                <div className="mt-4 space-y-2">
                  {carbonBreakdown.map((item) => (
                    <div key={item.scope} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-firmai-text text-xs">{item.scope}</span>
                      </div>
                      <span className="text-white text-xs font-medium">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Monthly Trend */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-sm md:text-base">年度能耗与碳排放趋势</h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-1 rounded-full bg-firmai-green" />
                    <span className="text-firmai-muted text-xs">能耗 (kWh)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-1 rounded-full bg-firmai-blue" />
                    <span className="text-firmai-muted text-xs">碳排放 (吨)</span>
                  </div>
                </div>
              </div>
              <div className="h-48">
                <svg width="100%" height="100%" viewBox="0 0 800 200" preserveAspectRatio="none">
                  {/* Energy line */}
                  <polyline
                    fill="none"
                    stroke="#00D9A3"
                    strokeWidth="2"
                    points={monthlyTrend.map((d, i) => {
                      const x = (i / (monthlyTrend.length - 1)) * 800;
                      const y = 180 - (d.energy / 500000) * 160;
                      return `${x},${y}`;
                    }).join(' ')}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Carbon line */}
                  <polyline
                    fill="none"
                    stroke="#00B4D8"
                    strokeWidth="2"
                    points={monthlyTrend.map((d, i) => {
                      const x = (i / (monthlyTrend.length - 1)) * 800;
                      const y = 180 - (d.carbon / 220) * 160;
                      return `${x},${y}`;
                    }).join(' ')}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {monthlyTrend.map((d, i) => {
                    const x = (i / (monthlyTrend.length - 1)) * 800;
                    return (
                      <text key={i} x={x} y={195} textAnchor="middle" fill="#6B7280" fontSize="9">
                        {d.month}
                      </text>
                    );
                  })}
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* ─── DEVICES TAB ─── */}
        {activeTab === 'devices' && (
          <div className="w-full px-4 md:px-8 lg:px-12 py-6">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl overflow-hidden">
              <div className="p-5 md:p-6 border-b border-firmai-border">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <h3 className="text-white font-semibold text-base">设备运行状态</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-firmai-green rounded-full" />
                      <span className="text-firmai-muted text-xs">在线 {deviceStatus.filter((d) => d.status === 'online').length}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-amber-400 rounded-full" />
                      <span className="text-firmai-muted text-xs">告警 {deviceStatus.filter((d) => d.status === 'warning').length}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-red-400 rounded-full" />
                      <span className="text-firmai-muted text-xs">离线 {deviceStatus.filter((d) => d.status === 'offline').length}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-firmai-border bg-firmai-dark/50">
                      <th className="text-left text-firmai-muted font-medium py-3 px-5">设备编号</th>
                      <th className="text-left text-firmai-muted font-medium py-3 px-5">设备名称</th>
                      <th className="text-left text-firmai-muted font-medium py-3 px-5">所属区域</th>
                      <th className="text-left text-firmai-muted font-medium py-3 px-5">状态</th>
                      <th className="text-right text-firmai-muted font-medium py-3 px-5">实时功率</th>
                      <th className="text-right text-firmai-muted font-medium py-3 px-5">运行效率</th>
                      <th className="text-right text-firmai-muted font-medium py-3 px-5">累计运行</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deviceStatus.map((device, idx) => (
                      <tr
                        key={device.id}
                        className={`border-b border-firmai-border/50 hover:bg-white/5 transition-colors ${idx % 2 === 0 ? 'bg-firmai-dark/20' : ''}`}
                      >
                        <td className="py-3 px-5 text-firmai-text">{device.id}</td>
                        <td className="py-3 px-5 text-white font-medium">{device.name}</td>
                        <td className="py-3 px-5 text-firmai-text">{device.area}</td>
                        <td className="py-3 px-5">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                            device.status === 'online'
                              ? 'bg-firmai-green/15 text-firmai-green border border-firmai-green/20'
                              : device.status === 'warning'
                              ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20'
                              : 'bg-red-500/15 text-red-400 border border-red-500/20'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              device.status === 'online' ? 'bg-firmai-green animate-pulse' : device.status === 'warning' ? 'bg-amber-400' : 'bg-red-400'
                            }`} />
                            {device.status === 'online' ? '在线' : device.status === 'warning' ? '告警' : '离线'}
                          </span>
                        </td>
                        <td className="py-3 px-5 text-right text-white font-mono">{device.power > 0 ? `${device.power} kW` : '-'}</td>
                        <td className="py-3 px-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 bg-firmai-border rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  device.efficiency >= 90 ? 'bg-firmai-green' : device.efficiency >= 80 ? 'bg-amber-400' : 'bg-red-400'
                                }`}
                                style={{ width: `${device.efficiency}%` }}
                              />
                            </div>
                            <span className="text-firmai-text text-xs w-8">{device.efficiency}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-5 text-right text-firmai-text">{(device.runtime / 24).toFixed(0)} 天</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── CARBON TAB ─── */}
        {activeTab === 'carbon' && (
          <div className="w-full px-4 md:px-8 lg:px-12 py-6 space-y-6">
            {/* Weekly Carbon */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6">
                <h3 className="text-white font-semibold text-sm md:text-base mb-4">本周碳排放 vs 目标（吨CO2）</h3>
                <div className="h-48">
                  <svg width="100%" height="100%" viewBox="0 0 500 180" preserveAspectRatio="none">
                    {weeklyCarbonData.map((d, i) => {
                      const x = (i / (weeklyCarbonData.length - 1)) * 480 + 10;
                      const actualH = (d.actual / 7) * 140;
                      const targetH = (d.target / 7) * 140;
                      const yActual = 160 - actualH;
                      const yTarget = 160 - targetH;
                      return (
                        <g key={d.day}>
                          <rect x={x - 15} y={yActual} width={12} height={actualH} rx={3} fill="#00D9A3" opacity={0.85} />
                          <rect x={x + 3} y={yTarget} width={12} height={targetH} rx={3} fill="#2A3441" />
                          <text x={x} y={175} textAnchor="middle" fill="#6B7280" fontSize="10">{d.day}</text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
                <div className="flex items-center justify-center gap-6 mt-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-firmai-green" />
                    <span className="text-firmai-muted text-xs">实际排放</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-firmai-border" />
                    <span className="text-firmai-muted text-xs">目标上限</span>
                  </div>
                </div>
              </div>

              {/* Carbon KPIs */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 grid grid-cols-2 gap-4">
                {[
                  { label: '本月碳排放', value: '156.8', unit: '吨', change: '-8.2%', color: 'green' },
                  { label: '年度累计', value: '1,847', unit: '吨', change: '-12.5%', color: 'green' },
                  { label: '碳强度', value: '0.42', unit: '吨/万元', change: '-15.3%', color: 'green' },
                  { label: '减排目标进度', value: '68', unit: '%', change: '提前2月', color: 'blue' },
                ].map((kpi, idx) => (
                  <div key={kpi.label} className="bg-firmai-card border border-firmai-border rounded-xl p-5">
                    <span className="text-firmai-muted text-xs">{kpi.label}</span>
                    <div className="text-white font-display font-bold text-xl md:text-2xl mt-2">
                      {kpi.value}<span className="text-sm font-normal text-firmai-muted"> {kpi.unit}</span>
                    </div>
                    <div className={`text-xs mt-1 ${kpi.color === 'green' ? 'text-firmai-green' : 'text-firmai-blue'}`}>
                      {kpi.change}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carbon Scope Detail */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6">
              <h3 className="text-white font-semibold text-sm md:text-base mb-4">碳排放范围明细</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {carbonBreakdown.map((scope) => (
                  <div key={scope.scope} className="bg-firmai-dark/50 border border-firmai-border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: scope.color }} />
                      <span className="text-white text-sm font-medium">{scope.scope}</span>
                    </div>
                    <div className="text-white font-display font-bold text-2xl">
                      {scope.value}<span className="text-sm font-normal text-firmai-muted">%</span>
                    </div>
                    <div className="mt-3 h-2 bg-firmai-border rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${scope.value}%`, backgroundColor: scope.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-12">
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700 max-w-2xl mx-auto text-center">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-white">想要这样的实时监测能力？</h2>
            <p className="text-firmai-text mt-3 text-base">
              Firmai 平台为您的企业提供同样的全方位能源与碳排放监测能力
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6">
              <button
                onClick={() => navigate('/pricing')}
                className="inline-flex items-center gap-2 px-8 py-3.5 border border-white/30 text-white font-medium rounded-md hover:border-firmai-green hover:text-firmai-green transition-all duration-200 whitespace-nowrap group"
              >
                查看定价方案
                <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
                  <i className="ri-arrow-right-line" />
                </span>
              </button>
              <a
                href="/#demo"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                  setTimeout(() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' }), 100);
                }}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-line" />
                </span>
                预约演示
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <ChatWidget />

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
}